import { Delete, X } from 'lucide-react';

const ROWS = ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

/* Toetsbreedte volgt de PANEELbreedte in plaats van een vaste maat: tien
   toetsen plus negen tussenruimtes van 0,25rem vullen samen exact 100%.

   Waarom dat moest: met w-9 (36px) was een rij van tien 414px breed, terwijl
   het paneel op een telefoon van 390px maar ~334px binnenwerk heeft. De rijen
   staan gecentreerd, dus die 80px staken er aan BEIDE kanten uit — links de
   "1" en rechts de "0" half afgeknipt, zonder scrollbalk om ze te bereiken.

   max-w-10 houdt de toetsen op een breed kassascherm op hun oude maat; daar
   verandert er dus niets. Alle rijen delen dezelfde toetsbreedte, dus de
   kortere rijen (9 en 7 toetsen) komen vanzelf ingesprongen te staan — precies
   zoals een echt toetsenbord. */
const KEY_W = 'w-[calc((100%-2.25rem)/10)] max-w-10';

/** Schermtoetsenbord — voor kassa- en touchscreenwerkplekken zonder fysiek
 *  toetsenbord. Overgenomen uit home.ejs; caret-afhandeling zit in SearchBox. */
export function VirtualKeyboard({ onKey, onClose }: { onKey: (k: string) => void; onClose: () => void }) {
  const Key = ({ label, value, wide }: { label: React.ReactNode; value: string; wide?: string }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onKey(value)}
      aria-label={`Toets ${typeof label === 'string' ? label : value}`}
      className={`flex h-10 min-w-0 items-center justify-center rounded-md border border-hairline-strong bg-raised text-base font-medium text-ink shadow-xs transition hover:border-accent hover:text-ink motion-safe:hover:-translate-y-0.5 ${wide ?? KEY_W}`}
    >
      {label}
    </button>
  );

  return (
    <div className="absolute top-full right-0 left-0 z-50 mt-2 rounded-lg border border-hairline bg-surface p-2 shadow-lg sm:p-3 motion-safe:animate-fade-down">
      <div className="flex flex-col gap-1.5">
        {ROWS.map((row) => (
          // w-full + justify-center: de rij moet de volle paneelbreedte hebben,
          // anders heeft de procentberekening in KEY_W niets om tegen af te
          // meten (een shrink-to-fit-rij zou circulair zijn).
          <div key={row} className="flex w-full justify-center gap-1">
            {[...row].map((ch) => (
              <Key key={ch} label={ch} value={ch} />
            ))}
          </div>
        ))}
        {/* Zelfde maximum als een volle lettertoetsrij (10 × max-w-10 plus negen
            tussenruimtes = 436px), zodat deze rij op een breed scherm niet
            buiten het toetsenblok uitsteekt. Op een telefoon halen de toetsen
            hun maximum niet en wint w-full — dan lopen alle rijen weer gelijk. */}
        <div className="mx-auto flex w-full max-w-[calc(2.5rem*10+2.25rem)] gap-1">
          {/* \b = backspace, \x00 = alles wissen (afgehandeld in SearchBox).
              De twee labels houden hun eigen breedte (shrink-0 + padding, dus
              "Wissen" komt nooit tegen zijn rand aan zoals bij een vaste w-20
              op 320px), en de spatiebalk slikt wat overblijft. Zo loopt deze
              rij op elk scherm van rand tot rand mee met de lettertoetsen. */}
          <Key label={<Delete size={15} />} value={'\b'} wide="basis-[15%] shrink-0" />
          <Key label="Spatie" value=" " wide="flex-1" />
          <Key label="Wissen" value={'\x00'} wide="shrink-0 px-3" />
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md py-1.5 text-sm text-ink-3 transition hover:bg-surface-hover hover:text-ink"
      >
        <X size={13} aria-hidden /> Toetsenbord sluiten
      </button>
    </div>
  );
}
